import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";

const LINKS = [
  { label: "What we test", href: "/#what-we-test" },
  { label: "How it works", href: "/#how-it-works" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faqs" },
];

export const Wordmark = ({ light = false }) => (
  <Link
    to="/"
    data-testid="nav-wordmark"
    className={`flex items-baseline gap-2 sm:gap-2.5 min-w-0 ${light ? "text-white" : "text-forest"}`}
    aria-label="UNSEEN — Home Health Lab, home"
  >
    <span className="text-[15px] sm:text-base font-bold uppercase tracking-[0.26em]">Unseen</span>
    <span className={`font-serif italic text-[15px] sm:text-base truncate ${light ? "text-white/70" : "text-sec"}`}>
      Home Health Lab
    </span>
  </Link>
);

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const MobilePanel = ({ onNavigate, onBooking }) => (
  <nav id="mobile-nav" data-testid="nav-mobile-panel" aria-label="Mobile" className="lg:hidden border-t border-divider bg-white animate-fade-up">
    <ul className="container-x py-2 divide-y divide-ink/[0.06]">
      {LINKS.map((l) => (
        <li key={l.href}>
          <a
            href={l.href}
            onClick={onNavigate}
            className="flex items-center justify-between py-4 min-h-[52px] text-base font-medium text-ink"
            data-testid={`nav-mobile-link-${slug(l.label)}`}
          >
            {l.label}
            <span aria-hidden className="text-sec">→</span>
          </a>
        </li>
      ))}
    </ul>
    {!onBooking && (
      <div className="container-x pb-5 pt-1">
        <Link to="/book" onClick={onNavigate} className="btn-primary" data-testid="nav-mobile-book-cta">
          Book assessment <ArrowRight size={18} />
        </Link>
      </div>
    )}
  </nav>
);

const DesktopLinks = () => (
  <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
    {LINKS.map((l) => (
      <a key={l.href} href={l.href} data-testid={`nav-link-${slug(l.label)}`} className="text-sm font-medium text-sec hover:text-ink transition-colors">
        {l.label}
      </a>
    ))}
  </nav>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const onBooking = pathname === "/book";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-[background-color,border-color] duration-300 ${scrolled || open ? "border-divider bg-white/92 backdrop-blur-md" : "border-transparent bg-white"}`}
      data-testid="site-nav"
    >
      <div className="container-x flex h-16 lg:h-20 items-center justify-between gap-4">
        <Wordmark />
        <DesktopLinks />
        <div className="flex items-center gap-2">
          {!onBooking && (
            <Link to="/book" data-testid="nav-book-cta" className="hidden lg:inline-flex btn-primary !py-2.5 !min-h-[44px] !px-5 !text-sm whitespace-nowrap">
              Book your assessment
            </Link>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-menu-toggle"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-divider text-ink hover:border-forest/40"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && <MobilePanel onNavigate={() => setOpen(false)} onBooking={onBooking} />}
    </header>
  );
};
